<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Add Room BackGround
        </v-card-title>

        <v-card-text>
          <!-- Cost & Duration Info (from company settings) -->
          <v-alert v-if="!isAdmin" type="info" dense class="mb-4">
            <v-row align="center">
              <v-col cols="auto">
                <v-icon>mdi-information</v-icon>
              </v-col>
              <v-col>
                <strong>Upload Cost:</strong> {{ companyCoin }} coins per image |
                <strong>Valid for:</strong> {{ companyDuration }} days |
                <strong>Your Balance:</strong> {{ userBalance }} coins
              </v-col>
            </v-row>
          </v-alert>

          <v-row class="mb-4">
            <v-col sm="4">
              <v-autocomplete
                v-model="form.room_id"
                :items="rooms"
                item-text="name"
                item-value="id"
                outlined
                label="Select Room (Optional)"
                prepend-inner-icon="mdi-door"
                clearable
              ></v-autocomplete>
            </v-col>
          </v-row>

          <!-- Balance Warning -->
          <v-alert
            v-if="!isAdmin && companyCoin > 0 && totalCost > userBalance"
            type="error"
            dense
            class="mb-4"
          >
            Insufficient balance! Required: {{ totalCost }} coins, Your balance: {{ userBalance }} coins
          </v-alert>

          <v-divider class="mb-4"></v-divider>

          <v-row>
            <v-col sm="12">
              <!-- Add Image Button -->
              <div class="mb-4">
                <v-btn
                  color="primary"
                  @click="addImageSlot"
                  :disabled="imageSlots.length >= maxImages"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Image Slot
                </v-btn>

                <v-btn
                  x-small
                  class="ml-2"
                  color="red"
                  dark
                  @click="removeAllImages"
                  v-if="imageSlots.length > 1"
                >
                  <v-icon left small>mdi-delete</v-icon>
                  Remove All
                </v-btn>

                <!-- Debug Button (Remove this in production) -->
                <v-btn x-small class="ml-2" color="grey" @click="debugSlots">
                  Debug
                </v-btn>

                <div class="upload-info mt-2">
                  <small class="text-muted">
                    Maximum {{ maxImages }} images allowed. Currently:
                    {{ imageSlots.length }} slots
                  </small>
                </div>
              </div>

              <!-- Image Upload Slots Grid -->
              <v-row>
                <v-col
                  v-for="(slot, index) in imageSlots"
                  :key="slot.id"
                  cols="12"
                  sm="6"
                  md="3"
                  class="image-slot-col"
                >
                  <!-- Background Name Input - Above Image -->
                  <v-text-field
                    v-model="slot.backgroundName"
                    :label="`Background Name ${index + 1}`"
                    placeholder="Enter name for this background"
                    dense
                    outlined
                    prepend-inner-icon="mdi-text"
                    class="mb-2 image-name-input"
                    :rules="[rules.required]"
                  ></v-text-field>
                  <v-card elevation="2" class="image-slot-card">
                    <div class="image-slot-container">
                      <!-- File Input (Hidden) -->
                      <input
                        type="file"
                        :id="`file-${slot.id}`"
                        :ref="`file-${slot.id}`"
                        accept="image/*"
                        @change="onFileChange($event, index)"
                        style="display: none"
                      />

                      <!-- Image Preview or Upload Area -->
                      <div
                        v-if="!slot.preview"
                        class="upload-area"
                        @click="triggerFileInput(slot.id)"
                      >
                        <v-icon size="48" color="grey lighten-1"
                          >mdi-camera-plus</v-icon
                        >
                        <p class="mt-2 text-center">Click to upload</p>
                        <small class="text-muted">Image {{ index + 1 }}</small>
                      </div>

                      <div v-if="slot.preview" class="image-preview-container">
                        <img
                          :src="slot.preview"
                          :alt="`Image ${index + 1}`"
                          class="preview-image"
                          @error="onImageError"
                        />

                        <!-- Image Overlay Actions -->
                        <div class="image-overlay">
                          <v-btn
                            fab
                            x-small
                            color="blue"
                            dark
                            class="action-btn"
                            @click="viewImage(slot.preview)"
                          >
                            <v-icon color="primary" small>mdi-eye</v-icon>
                          </v-btn>

                          <v-btn
                            fab
                            x-small
                            color="orange"
                            dark
                            class="action-btn"
                            @click="changeImage(slot.id)"
                          >
                            <v-icon color="primary" small>mdi-camera</v-icon>
                          </v-btn>
                        </div>

                        <!-- Image Info -->
                        <div class="image-info">
                          <div class="image-name">{{ slot.fileName || slot.name }}</div>
                          <div class="image-size">
                            {{ formatFileSize(slot.size) }}
                          </div>
                        </div>
                      </div>

                      <!-- Background Name Input for each image -->
                      <div v-if="slot.preview" class="mt-2">
                        <v-text-field
                          v-model="slot.backgroundName"
                          label="Background Name"
                          placeholder="Enter name for this background"
                          dense
                          outlined
                          prepend-inner-icon="mdi-text"
                          class="image-name-input"
                        ></v-text-field>
                      </div>

                      <!-- Remove Slot Button -->
                      <v-btn
                        fab
                        x-small
                        color="red"
                        dark
                        class="remove-slot-btn"
                        @click="removeImageSlot(index)"
                        v-if="imageSlots.length > 1"
                      >
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-divider class="my-5"></v-divider>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <v-btn
              rounded
              color="primary"
              :loading="isLoading"
              :disabled="isLoading || !hasImages"
              @click="save"
              class="mr-3"
            >
              <v-icon left>mdi-content-save</v-icon>
              Save Images ({{ uploadedImagesCount }})
            </v-btn>

            <v-btn
              rounded
              outlined
              color="grey"
              @click="$router.back()"
              :disabled="isLoading"
            >
              <v-icon left>mdi-cancel</v-icon>
              Cancel
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Image View Dialog -->
    <v-dialog v-model="imageDialog" max-width="800">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Image Preview</span>
          <v-btn icon @click="imageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <img :src="selectedImageUrl" style="width: 100%; height: auto" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "BannersCreatePage",
  data() {
    return {
      form: {
        name: "",
        title: "",
        type: "",
        description: "",
        date: "",
        room_id: null,
      },
      rules: {
        required: (value) => !!value || 'This field is required',
      },
      isLoading: false,
      imageSlots: [
        {
          id: 1,
          file: null,
          preview: null,
          name: "",
          fileName: "",
          backgroundName: "",
          size: 0,
        },
      ],
      maxImages: 20,
      imageDialog: false,
      selectedImageUrl: "",
      banners: null,
      slotCounter: 1,
      userBalance: 0,
      rooms: [],
      isAdmin: false,
      companyCoin: 0,
      companyDuration: 7,
    };
  },
  computed: {
    hasImages() {
      return this.imageSlots.some((slot) => slot.file !== null);
    },
    uploadedImagesCount() {
      return this.imageSlots.filter((slot) => slot.file !== null).length;
    },
    totalCost() {
      return this.companyCoin * this.uploadedImagesCount;
    },
  },
  mounted() {
    this.getUserBalance();
    this.getRooms();
    this.checkIsAdmin();
    this.getCompanySettings();
  },
  methods: {
    addImageSlot() {
      if (this.imageSlots.length < this.maxImages) {
        this.slotCounter++;
        this.imageSlots.push({
          id: this.slotCounter,
          file: null,
          preview: null,
          name: "",
          fileName: "",
          backgroundName: "",
          size: 0,
        });
      }
    },

    removeImageSlot(index) {
      if (this.imageSlots.length > 1) {
        this.imageSlots.splice(index, 1);
      }
    },

    removeAllImages() {
      if (confirm("Are you sure you want to remove all image slots?")) {
        this.imageSlots = [
          {
            id: ++this.slotCounter,
            file: null,
            preview: null,
            name: "",
            fileName: "",
            backgroundName: "",
            size: 0,
          },
        ];
      }
    },

    triggerFileInput(slotId) {
      const fileInput = document.getElementById(`file-${slotId}`);
      if (fileInput) {
        fileInput.click();
      }
    },

    changeImage(slotId) {
      this.triggerFileInput(slotId);
    },

    onFileChange(event, slotIndex) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert(`${file.name} is not a valid image file`);
        return;
      }

      // Validate file size (max 10MB per image)
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum size is 10MB`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        // Use Vue.set to ensure reactivity or use spread operator
        this.$set(this.imageSlots, slotIndex, {
          ...this.imageSlots[slotIndex],
          file: file,
          preview: e.target.result,
          name: file.name,
          fileName: file.name,
          size: file.size,
        });

        console.log("Image loaded:", {
          name: file.name,
          size: file.size,
          preview: e.target.result ? "Preview available" : "No preview",
        });
      };

      reader.onerror = (error) => {
        console.error("Error reading file:", error);
        alert(`Error reading ${file.name}`);
      };

      reader.readAsDataURL(file);

      // Clear the input to allow re-selecting the same file
      event.target.value = "";
    },

    async save() {
      const imagesToUpload = this.imageSlots.filter(
        (slot) => slot.file !== null
      );

      if (imagesToUpload.length === 0) {
        this.$toast.error("Please select at least one image");
        return;
      }

      try {
        this.isLoading = true;

        // Validate that each image has a background name
        for (let i = 0; i < imagesToUpload.length; i++) {
          const slot = imagesToUpload[i];
          if (!slot.backgroundName || slot.backgroundName.trim() === '') {
            this.$toast.error(`Please enter a background name for image ${i + 1}`);
            this.isLoading = false;
            return;
          }
        }

        // Prepare FormData
        const formData = new FormData();
        formData.append("room_id", this.form.room_id || "");
        formData.append("created_by_id", this.form.created_by_id || "");

        imagesToUpload.forEach((slot, index) => {
          formData.append(`backgrounds[${index}][background_path]`, slot.file);
          formData.append(`backgrounds[${index}][name]`, slot.backgroundName.trim());
          console.log("index", index, "file:", slot.file, "name:", slot.backgroundName);
        });

        // Send request to backend
        const response = await axios.post("/room_backgrounds", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        console.log("response", response);
        this.isLoading = false;
        this.$router.back();
      } catch (error) {
        console.error(error);
        this.isLoading = false;
      }
    },

    async handleMultipleFileUpload(imagesToUpload) {
      const uploadPromises = [];

      imagesToUpload.forEach((slot, index) => {
        const formData = new FormData();
        formData.append("id", this.banners.id);
        formData.append("image_path", slot.file);
        formData.append("image_order", index);

        const uploadPromise = axios
          .post("upload_banners_image", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .catch(function (error) {
            console.log(error);
            console.log("FAILURE!!");
          });

        uploadPromises.push(uploadPromise);
      });

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);
    },

    viewImage(imageUrl) {
      this.selectedImageUrl = imageUrl;
      this.imageDialog = true;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";

      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    onImageError() {
      console.error("Image failed to load");
    },

    // Debug method to check slot data
    debugSlots() {
      console.log("Current image slots:", this.imageSlots);
    },

    // Placeholder for saveUserTimestamp method
    async saveUserTimestamp(param1, param2) {
      console.log("Saving user timestamp...", param1 + param2);
    },

    async getUserBalance() {
      try {
        const response = await axios.get("/wallets?wallet_type=0");
        if (response.data.data && response.data.data.length > 0) {
          this.userBalance = response.data.data[0].balance || 0;
        }
      } catch (error) {
        console.error("Error fetching balance:", error);
      }
    },

    async getRooms() {
      try {
        const response = await axios.get("/rooms?is_active=1");
        this.rooms = response.data.data || [];
      } catch (error) {
        console.error("Error fetching rooms:", error);
      }
    },

    checkIsAdmin() {
      const user = this.$store.getters["auth/user"];
      if (user && user.roles) {
        this.isAdmin = user.roles.some(
          (role) => role.name === "ADMIN" || role.name === "SUPER ADMIN"
        );
      }
    },

    getCompanySettings() {
      const company = this.$store.getters["auth/company"];
      if (company) {
        this.companyCoin = company.room_background_upload_coin || 0;
        this.companyDuration = company.room_background_duration_days || 7;
      }
    },
  },
};
</script>

<style scoped>
.image-slot-col {
  padding: 8px;
}

.image-slot-card {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  height: 220px;
}

.image-slot-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  background-color: #fafafa;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.upload-area:hover {
  background-color: #f0f0f0;
  border-color: #2196f3;
}

.image-preview-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-preview-container:hover .preview-image {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-preview-container:hover .image-overlay {
  opacity: 1;
}

.action-btn {
  backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.remove-slot-btn {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
}

.image-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 20px 12px 12px;
  font-size: 0.8rem;
}

.image-name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-size {
  opacity: 0.8;
  margin-top: 2px;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 16px;
}

.upload-info {
  margin-top: 8px;
}

/* Animation for new slots */
.image-slot-col {
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .image-slot-card {
    height: 180px;
  }
}

@media (max-width: 600px) {
  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons .v-btn {
    width: 100%;
    margin: 4px 0;
  }

  .image-slot-card {
    height: 160px;
  }
}
</style>
